import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import HistoryIcon from "@mui/icons-material/History";
import AdminBookingHistory from "./AdminBookingHistory";
import dayjs from "dayjs";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { searchAPI } from "../API/Seach";
import { authAPI } from "../API/Auth";
import { tokens } from "../theme/tokens";

const DRAWER_WIDTH = 268;

/** Matches `Header` Toolbar: minHeight xs 72 / md 80 plus `py:1` (8px top + bottom). */
const HEADER_OFFSET_XS = "88px";
const HEADER_OFFSET_MD = "80px";

const INITIAL_ADD_USER_FORM = {
  userName: "",
  password: "",
  email: "",
  phoneNumber: "",
  role: "user",
  isMasterUser: false,
};

const initialWalletRows = [
  {
    id: "w1",
    user: "Asha Verma",
    type: "Debit",
    amount: 12400,
    date: "2026-03-28",
    description: "Hotel booking — BLR",
  },
  {
    id: "w2",
    user: "Rahul Mehta",
    type: "Credit",
    amount: 50000,
    date: "2026-03-25",
    description: "Wallet top-up",
  },
  {
    id: "w3",
    user: "Priya Nair",
    type: "Debit",
    amount: 8900,
    date: "2026-03-22",
    description: "Hotel booking — GOI",
  },
];

function StatCard({ title, value, subtitle }) {
  return (
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${tokens.border}`,
        borderRadius: `${tokens.radiusLg}px`,
        boxShadow: tokens.shadowSm,
        height: "100%",
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Typography variant="body2" sx={{ color: tokens.textMuted, fontWeight: 600, mb: 0.5 }}>
          {title}
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontFamily: tokens.fontDisplay,
            fontWeight: 700,
            color: tokens.brand,
            letterSpacing: "-0.02em",
          }}
        >
          {value}
        </Typography>
        {subtitle ? (
          <Typography variant="caption" sx={{ color: tokens.textSubtle, display: "block", mt: 1 }}>
            {subtitle}
          </Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

export default function AdminPanel() {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [section, setSection] = useState("dashboard");
  const [clientUsers, setClientUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState("");
  const [walletRows] = useState(initialWalletRows);
  const [bookingsThisMonth, setBookingsThisMonth] = useState(0);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addUserSubmitting, setAddUserSubmitting] = useState(false);
  const [newUser, setNewUser] = useState(INITIAL_ADD_USER_FORM);
  const [clientBalanceAmount, setClientBalanceAmount] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);

  const formatClientBalance = (value) => {
    if (value == null || Number.isNaN(Number(value))) return "—";
    return `₹${Number(value).toLocaleString("en-IN")}`;
  };

  const fetchBookingsCount = useCallback(async () => {
    const rawId = localStorage.getItem("userId");
    const userId = typeof rawId === "string" ? parseInt(rawId, 10) : Number(rawId);
    if (!userId || Number.isNaN(userId)) {
      setBookingsThisMonth(0);
      setLoadingBookings(false);
      return;
    }
    try {
      setLoadingBookings(true);
      const response = await searchAPI.getBookingList({ userId, fromDate: null, toDate: null });
      if (response.status && response.code === 200 && Array.isArray(response.data)) {
        const count = response.data.filter((b) => {
          const d = b.bookingDate ? dayjs(b.bookingDate) : null;
          return d && d.isValid() && d.isSame(dayjs(), "month") && d.isSame(dayjs(), "year");
        }).length;
        setBookingsThisMonth(count);
      } else {
        setBookingsThisMonth(0);
      }
    } catch {
      setBookingsThisMonth(0);
    } finally {
      setLoadingBookings(false);
    }
  }, []);

  const fetchClientUsers = useCallback(async () => {
    const cidRaw = localStorage.getItem("clientId");
    const clientId = cidRaw ? parseInt(cidRaw, 10) : NaN;
    if (!cidRaw || Number.isNaN(clientId)) {
      setUsersError("Client ID not found. Please sign in again.");
      setClientUsers([]);
      setUsersLoading(false);
      return;
    }
    setUsersError("");
    setUsersLoading(true);
    try {
      const res = await authAPI.getClientUsers(clientId);
      const ok =
        res &&
        String(res.status || "").toLowerCase() === "success" &&
        (res.statusCode === 200 || res.statuscode === 200);
      if (ok && Array.isArray(res.data)) {
        setClientUsers(res.data);
      } else {
        setClientUsers([]);
        toast.error(res?.message || "Failed to load users.");
      }
    } catch (err) {
      setClientUsers([]);
      const msg = err?.message || "Failed to load users.";
      toast.error(msg);
      setUsersError(msg);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const fetchClientBalance = useCallback(async () => {
    const cidRaw = localStorage.getItem("clientId");
    const clientId = cidRaw ? parseInt(cidRaw, 10) : NaN;
    if (!cidRaw || Number.isNaN(clientId)) {
      setClientBalanceAmount(null);
      setBalanceLoading(false);
      return;
    }
    setBalanceLoading(true);
    try {
      const res = await authAPI.getClientBalance(clientId);
      const ok =
        res &&
        String(res.status || "").toLowerCase() === "success" &&
        (res.statusCode === 200 || res.statuscode === 200);
      if (ok && res.data) {
        setClientBalanceAmount(res.data.balanceAmount);
      } else {
        setClientBalanceAmount(null);
      }
    } catch {
      setClientBalanceAmount(null);
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookingsCount();
  }, [fetchBookingsCount]);

  useEffect(() => {
    fetchClientUsers();
  }, [fetchClientUsers]);

  useEffect(() => {
    fetchClientBalance();
  }, [fetchClientBalance]);

  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: <DashboardIcon fontSize="small" /> },
    { id: "users", label: "Users", icon: <PeopleIcon fontSize="small" /> },
    { id: "booking-history", label: "Booking history", icon: <HistoryIcon fontSize="small" /> },
    { id: "wallet", label: "Wallet", icon: <AccountBalanceWalletIcon fontSize="small" /> },
  ];

  const drawer = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background: `linear-gradient(180deg, ${tokens.brandDeep} 0%, ${tokens.brand} 55%, ${tokens.brandMuted} 100%)`,
        color: "#fff",
      }}
    >
      <Box sx={{ px: 2.5, py: 3, borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
        <Typography
          variant="overline"
          sx={{ letterSpacing: "0.14em", opacity: 0.85, fontWeight: 700, display: "block" }}
        >
          Administration
        </Typography>
        <Typography variant="h6" sx={{ fontFamily: tokens.fontDisplay, fontWeight: 700, mt: 0.5 }}>
          Admin Panel
        </Typography>
      </Box>
      <List sx={{ px: 1.5, py: 2, flex: 1 }}>
        {navItems.map((item) => (
          <ListItemButton
            key={item.id}
            selected={section === item.id}
            onClick={() => {
              setSection(item.id);
              setMobileOpen(false);
            }}
            sx={{
              borderRadius: `${tokens.radiusMd}px`,
              mb: 0.5,
              "&.Mui-selected": {
                bgcolor: "rgba(196, 163, 90, 0.22)",
                "&:hover": { bgcolor: "rgba(196, 163, 90, 0.28)" },
              },
              "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
            }}
          >
            <ListItemIcon sx={{ color: tokens.highlight, minWidth: 40 }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{ fontWeight: 600, fontSize: "0.95rem" }}
            />
          </ListItemButton>
        ))}
      </List>
      <Box
        sx={{
          p: 2,
          borderTop: "1px solid rgba(255,255,255,0.12)",
          mt: "auto",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: 0.9 }}>
          <TrendingUpIcon sx={{ fontSize: 20, color: tokens.highlight }} />
          <Box>
            <Typography variant="caption" sx={{ display: "block", fontWeight: 700, letterSpacing: "0.04em" }}>
              Quick tip
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85, lineHeight: 1.35 }}>
              Review wallet usage weekly to catch unusual debits early.
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Box>
  );

  const openAddUserModal = () => {
    setNewUser({ ...INITIAL_ADD_USER_FORM });
    setAddOpen(true);
  };

  const handleAddUserSubmit = async () => {
    if (!newUser.userName.trim()) {
      toast.error("Username is required.");
      return;
    }
    if (!newUser.password) {
      toast.error("Password is required.");
      return;
    }
    if (!newUser.email.trim()) {
      toast.error("Email is required.");
      return;
    }
    if (!newUser.phoneNumber.trim()) {
      toast.error("Phone number is required.");
      return;
    }

    const cidRaw = localStorage.getItem("clientId");
    const clientId = cidRaw ? parseInt(cidRaw, 10) : NaN;
    const parentRaw = localStorage.getItem("userId");
    const parentId = parentRaw ? parseInt(parentRaw, 10) : NaN;

    if (!cidRaw || Number.isNaN(clientId)) {
      toast.error("Client ID missing. Please sign in again.");
      return;
    }
    if (!parentRaw || Number.isNaN(parentId)) {
      toast.error("User ID missing. Please sign in again.");
      return;
    }

    const payload = {
      clientId,
      userName: newUser.userName.trim(),
      password: newUser.password,
      email: newUser.email.trim(),
      phoneNumber: newUser.phoneNumber.trim(),
      role: newUser.role,
      parentId,
      isMasterUser: Boolean(newUser.isMasterUser),
      createdBy: "admin",
    };

    setAddUserSubmitting(true);
    try {
      const res = await authAPI.createUser(payload);
      const failed =
        res &&
        (res.success === false ||
          String(res.status || "").toLowerCase() === "error" ||
          (typeof res.statusCode === "number" && res.statusCode >= 400));
      if (failed) {
        toast.error(res?.message || "Failed to create user.");
        return;
      }
      toast.success(res?.message || "User created successfully.");
      setAddOpen(false);
      setNewUser({ ...INITIAL_ADD_USER_FORM });
      await fetchClientUsers();
    } catch (err) {
      const msg =
        (typeof err?.message === "string" && err.message) ||
        err?.title ||
        (Array.isArray(err?.errors) && err.errors.join(", ")) ||
        "Failed to create user.";
      toast.error(msg);
    } finally {
      setAddUserSubmitting(false);
    }
  };

  const formatUserDate = (value) => {
    if (!value) return "—";
    const d = dayjs(value);
    return d.isValid() ? d.format("DD MMM YYYY") : "—";
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={2800} />
      <Box
        sx={{
          display: "flex",
          alignItems: "stretch",
          bgcolor: tokens.surface,
          mt: { xs: HEADER_OFFSET_XS, md: HEADER_OFFSET_MD },
          minHeight: { xs: `calc(100vh - ${HEADER_OFFSET_XS})`, md: `calc(100vh - ${HEADER_OFFSET_MD})` },
        }}
      >
        <Box
          component="nav"
          sx={{
            width: { md: DRAWER_WIDTH },
            flexShrink: { md: 0 },
            display: { md: "flex" },
            flexDirection: { md: "column" },
            alignSelf: "stretch",
            minHeight: { xs: `calc(100vh - ${HEADER_OFFSET_XS})`, md: `calc(100vh - ${HEADER_OFFSET_MD})` },
          }}
        >
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: "block", md: "none" },
              [`& .MuiDrawer-paper`]: {
                width: DRAWER_WIDTH,
                boxSizing: "border-box",
                border: "none",
                top: HEADER_OFFSET_XS,
                height: `calc(100vh - ${HEADER_OFFSET_XS})`,
                maxHeight: `calc(100vh - ${HEADER_OFFSET_XS})`,
              },
            }}
          >
            {drawer}
          </Drawer>
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              width: DRAWER_WIDTH,
              flexShrink: 0,
              flex: { md: "0 0 auto" },
              alignSelf: "stretch",
              minHeight: { md: `calc(100vh - ${HEADER_OFFSET_MD})` },
              height: { md: `calc(100vh - ${HEADER_OFFSET_MD})` },
              maxHeight: { md: `calc(100vh - ${HEADER_OFFSET_MD})` },
              borderRight: `1px solid ${tokens.border}`,
              borderRadius: 0,
              overflow: "hidden",
            }}
          >
            {drawer}
          </Box>
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 3 },
            width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
            minWidth: 0,
            minHeight: { xs: `calc(100vh - ${HEADER_OFFSET_XS})`, md: `calc(100vh - ${HEADER_OFFSET_MD})` },
          }}
        >
          {!isMdUp ? (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <IconButton
                onClick={() => setMobileOpen(true)}
                aria-label="Open admin menu"
                sx={{
                  border: `1px solid ${tokens.border}`,
                  borderRadius: `${tokens.radiusMd}px`,
                  color: tokens.brand,
                }}
              >
                <MenuIcon />
              </IconButton>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: tokens.text }}>
                {navItems.find((n) => n.id === section)?.label}
              </Typography>
            </Stack>
          ) : null}

          {section === "dashboard" && (
            <Stack spacing={3}>
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: tokens.fontDisplay,
                    fontWeight: 700,
                    color: tokens.brand,
                  }}
                >
                  Dashboard
                </Typography>
                <Typography variant="body2" sx={{ color: tokens.textMuted, mt: 0.5 }}>
                  Overview for your B2B workspace. Booking count reflects your account&apos;s trips this month.
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
                  gap: 2,
                }}
              >
                <StatCard
                  title="Total users"
                  value={usersLoading ? "…" : clientUsers.length}
                  subtitle="Users returned for your client from the API"
                />
                <StatCard
                  title="Bookings this month"
                  value={loadingBookings ? "…" : bookingsThisMonth}
                  subtitle="From your linked booking history"
                />
                <StatCard
                  title="Wallet balance"
                  value={balanceLoading ? "…" : formatClientBalance(clientBalanceAmount)}
                  subtitle="Client balance from API (balanceAmount)"
                />
              </Box>

              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: `${tokens.radiusLg}px`,
                  border: `1px solid ${tokens.border}`,
                  boxShadow: tokens.shadowSm,
                }}
              >
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, color: tokens.brand }}>
                  Activity snapshot
                </Typography>
                <Stack spacing={1.25}>
                  <Typography variant="body2" sx={{ color: tokens.textMuted }}>
                    • {usersLoading ? "…" : clientUsers.filter((u) => !u.isActive).length} inactive user(s) in your client
                  </Typography>
                  <Typography variant="body2" sx={{ color: tokens.textMuted }}>
                    • Wallet balance:{" "}
                    {balanceLoading ? "…" : formatClientBalance(clientBalanceAmount)} (live client balance)
                  </Typography>
                  <Typography variant="body2" sx={{ color: tokens.textMuted }}>
                    • Use the sidebar to manage users or inspect wallet usage in detail.
                  </Typography>
                </Stack>
              </Paper>
            </Stack>
          )}

          {section === "users" && (
            <Stack spacing={2}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Box>
                  <Typography variant="h5" sx={{ fontFamily: tokens.fontDisplay, fontWeight: 700, color: tokens.brand }}>
                    Users
                  </Typography>
                  <Typography variant="body2" sx={{ color: tokens.textMuted, mt: 0.5 }}>
                    Users for client ID {localStorage.getItem("clientId") || "—"} (from API).
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    variant="outlined"
                    onClick={() => fetchClientUsers()}
                    disabled={usersLoading}
                    sx={{ borderRadius: `${tokens.radiusXl}px`, textTransform: "none" }}
                  >
                    Refresh
                  </Button>
                  <Button variant="contained" color="primary" onClick={openAddUserModal} sx={{ borderRadius: `${tokens.radiusXl}px` }}>
                    Add user
                  </Button>
                </Stack>
              </Stack>

              {usersError && !usersLoading ? (
                <Typography variant="body2" sx={{ color: tokens.error }}>
                  {usersError}
                </Typography>
              ) : null}

              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  border: `1px solid ${tokens.border}`,
                  borderRadius: `${tokens.radiusLg}px`,
                  overflowX: "auto",
                }}
              >
                {usersLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress sx={{ color: tokens.accent }} />
                  </Box>
                ) : (
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ bgcolor: tokens.surfaceAlt }}>
                        <TableCell sx={{ fontWeight: 700 }}>User ID</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Username</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Phone number</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Active</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Master user</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Created date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clientUsers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 4, color: tokens.textMuted }}>
                            No users found.
                          </TableCell>
                        </TableRow>
                      ) : (
                        clientUsers.map((row) => (
                          <TableRow key={row.userId} hover>
                            <TableCell>{row.userId}</TableCell>
                            <TableCell>{row.userName}</TableCell>
                            <TableCell>{row.email}</TableCell>
                            <TableCell>{row.phoneNumber ?? "—"}</TableCell>
                            <TableCell sx={{ textTransform: "capitalize" }}>{row.role}</TableCell>
                            <TableCell>
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: row.isActive ? tokens.success : tokens.textMuted,
                                }}
                              >
                                {row.isActive ? "Yes" : "No"}
                              </Typography>
                            </TableCell>
                            <TableCell>{row.isMasterUser ? "Yes" : "No"}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{formatUserDate(row.createdDate)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                )}
              </TableContainer>
            </Stack>
          )}

          {section === "booking-history" && (
            <AdminBookingHistory clientUsers={clientUsers} clientUsersLoading={usersLoading} />
          )}

          {section === "wallet" && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="h5" sx={{ fontFamily: tokens.fontDisplay, fontWeight: 700, color: tokens.brand }}>
                  Wallet
                </Typography>
                <Typography variant="body2" sx={{ color: tokens.textMuted, mt: 0.5 }}>
                  Ledger of credits and debits (demo data).
                </Typography>
              </Box>

              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: `1px solid ${tokens.border}`, borderRadius: `${tokens.radiusLg}px` }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: tokens.surfaceAlt }}>
                      <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Amount
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {walletRows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.user}</TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ color: row.type === "Credit" ? tokens.success : tokens.error }}
                          >
                            {row.type}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">₹{row.amount.toLocaleString("en-IN")}</TableCell>
                        <TableCell>{dayjs(row.date).format("DD MMM YYYY")}</TableCell>
                        <TableCell>{row.description}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          )}
        </Box>
      </Box>

      <Dialog
        open={addOpen}
        onClose={() => {
          if (!addUserSubmitting) setAddOpen(false);
        }}
        fullWidth
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: { borderRadius: `${tokens.radiusLg}px`, border: `1px solid ${tokens.border}` },
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: tokens.fontDisplay, fontWeight: 700, pr: 6 }}>
          Add user
          <IconButton
            aria-label="Close"
            onClick={() => !addUserSubmitting && setAddOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8, color: tokens.textMuted }}
            disabled={addUserSubmitting}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: tokens.border }}>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Username"
              fullWidth
              required
              autoComplete="username"
              value={newUser.userName}
              onChange={(e) => setNewUser((p) => ({ ...p, userName: e.target.value }))}
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              required
              autoComplete="new-password"
              value={newUser.password}
              onChange={(e) => setNewUser((p) => ({ ...p, password: e.target.value }))}
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              autoComplete="email"
              value={newUser.email}
              onChange={(e) => setNewUser((p) => ({ ...p, email: e.target.value }))}
            />
            <TextField
              label="Phone number"
              fullWidth
              required
              value={newUser.phoneNumber}
              onChange={(e) => setNewUser((p) => ({ ...p, phoneNumber: e.target.value }))}
            />
            <TextField
              select
              label="Role"
              fullWidth
              required
              value={newUser.role}
              onChange={(e) => setNewUser((p) => ({ ...p, role: e.target.value }))}
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </TextField>
            <TextField
              select
              label="Master user"
              fullWidth
              required
              value={newUser.isMasterUser ? "yes" : "no"}
              onChange={(e) => setNewUser((p) => ({ ...p, isMasterUser: e.target.value === "yes" }))}
            >
              <MenuItem value="no">No</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => !addUserSubmitting && setAddOpen(false)} sx={{ color: tokens.textMuted }} disabled={addUserSubmitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAddUserSubmit} disabled={addUserSubmitting}>
            {addUserSubmitting ? "Saving…" : "Save user"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
